import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from 'react';
import { Check, Minus } from 'lucide-react';

import { cn } from '@/lib/utils';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string;
  description?: string;
  error?: string;
  indeterminate?: boolean;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, error, indeterminate, className = '', id, disabled, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const internalRef = useRef<HTMLInputElement>(null);
    const resolvedRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate, resolvedRef]);

    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <label
          htmlFor={inputId}
          className={cn(
            'group inline-flex items-start gap-3 cursor-pointer select-none',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <span className="relative inline-flex items-center justify-center mt-0.5 shrink-0 w-5 h-5">
            <input
              ref={resolvedRef}
              id={inputId}
              type="checkbox"
              disabled={disabled}
              className="peer absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
              {...props}
            />
            <span
              className={cn(
                'absolute inset-0 rounded border-2 transition-all duration-200',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-900',
                error
                  ? 'border-red-500 peer-checked:bg-red-500 peer-indeterminate:bg-red-500'
                  : cn(
                      'border-surface-600 peer-checked:border-brand-500 peer-checked:bg-brand-500',
                      'peer-indeterminate:border-brand-500 peer-indeterminate:bg-brand-500',
                      !disabled && 'group-hover:border-surface-500',
                    ),
              )}
            />
            <Check
              className="absolute w-3.5 h-3.5 text-surface-900 pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity duration-150"
              strokeWidth={3}
            />
            {indeterminate && (
              <Minus
                className="absolute w-3.5 h-3.5 text-surface-900 pointer-events-none"
                strokeWidth={3}
              />
            )}
          </span>
          {(label || description) && (
            <span className="flex flex-col">
              {label && <span className="text-sm font-medium text-white">{label}</span>}
              {description && <span className="text-sm text-content-muted">{description}</span>}
            </span>
          )}
        </label>
        {error && <p className="text-sm text-red-500 ml-8">{error}</p>}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
